/**
 * CentralUnavailableOverlay
 *
 * Non-dismissible full-screen overlay shown when BootGate cannot reach Central.
 * z-[9999] — highest overlay, above WorkspaceClosedOverlay.
 *
 * Provides a Retry button that re-attempts the switch-context call.
 * No auto-retry, no loops — user must click Retry manually.
 */

import { useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import type { AppDispatch } from '../../store';
import {
  selectCentralAccessToken,
  setBootReady,
  setBootWorkspaceClosed,
  resetBoot,
  updateCentralAccessToken,
} from '../../store/slices/centralAuthSlice';
import { CENTRAL_URL, APP_ID } from '../../config/centralAuth';

export function CentralUnavailableOverlay() {
  const dispatch = useDispatch<AppDispatch>();
  const accessToken = useSelector(selectCentralAccessToken);
  const [retrying, setRetrying] = useState(false);

  const handleRetry = useCallback(async () => {
    if (!accessToken || retrying) return;
    setRetrying(true);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30_000);

    try {
      const response = await fetch(`${CENTRAL_URL}/api/suite/switch-context`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-App-Id': APP_ID,
        },
        signal: controller.signal,
      });

      clearTimeout(timeout);

      if (response.ok) {
        const data = await response.json();
        // Update token if switch-context returns a new one
        if (data.accessToken) {
          dispatch(updateCentralAccessToken({
            accessToken: data.accessToken,
            expiresIn: data.expiresIn,
          }));
        }
        dispatch(resetBoot());
        dispatch(
          setBootReady({
            currentWorkspace: data.workspace,
            otherWorkspaces: data.otherWorkspaces ?? [],
            apps: data.apps ?? [],
            user: data.user ?? null,
            meta: data.meta,
          })
        );
        return;
      }

      // 401: token rejected — show inline error, do NOT logout.
      // User can sign out manually from the overlay or from the main UI.
      if (response.status === 401) {
        console.warn('[CentralUnavailableOverlay] Retry got 401 — token expired');
        // Still unavailable (don't clear overlay)
        return;
      }

      if (response.status === 409) {
        const data = await response.json().catch(() => ({}));
        if (data.code === 'WORKSPACE_CLOSED') {
          dispatch(resetBoot());
          dispatch(setBootWorkspaceClosed({ otherWorkspaces: data.otherWorkspaces ?? [] }));
          return;
        }
      }

      // Still unavailable
      console.error('[CentralUnavailableOverlay] Retry failed:', response.status);
    } catch (err) {
      clearTimeout(timeout);
      console.error('[CentralUnavailableOverlay] Retry error:', err);
    } finally {
      setRetrying(false);
    }
  }, [accessToken, retrying, dispatch]);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-900/80 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-8">
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0-10.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.25-8.25-3.286Z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Service Unavailable</h2>
          <p className="mt-2 text-sm text-gray-600">
            Unable to verify your workspace with Central Identity.
            This may be a temporary issue.
          </p>
        </div>

        <button
          onClick={handleRetry}
          disabled={retrying}
          className="w-full px-4 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {retrying ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Retrying…
            </span>
          ) : (
            'Retry'
          )}
        </button>
      </div>
    </div>
  );
}
